import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from 'react';
import * as Sentry from '@sentry/react';
import { CognitoAccessToken, InitializeAcc } from '@Models/types';
import { jwtDecode } from 'jwt-decode';
import { accountsAPI } from '@Lib/axios/account';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { signInWithAzureAD } from '@Auth/amplify';
import { toast } from 'react-hot-toast';

type AuthContextType = {
  user: InitializeAcc | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  initializeUser: (force?: boolean) => Promise<void>;
  userHasAPIKeys: boolean;
  loggingIn: boolean;
  setLoggingIn: React.Dispatch<React.SetStateAction<boolean>>;
  refreshIfExpired: (token: string | null) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';
const MOCK_USER: InitializeAcc = {
  admin: true,
  profile: {
    email: 'local@dev',
    username: 'local-dev',
    name: 'Local Dev',
    aha_email: '',
    aha_api_key: '',
    testrail_username: '',
    testrail_api_key: '',
  },
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<InitializeAcc | null>(() => {
    if (BYPASS_AUTH) return MOCK_USER;
    const cachedUser = localStorage.getItem('user');
    return cachedUser ? JSON.parse(cachedUser) : null;
  });
  const [loggingIn, setLoggingIn] = useState(false);

  const login = async () => {
    if (BYPASS_AUTH) {
      setUser(MOCK_USER);
      return;
    }
    setLoggingIn(true);
    try {
      await signInWithAzureAD();
    } catch (err) {
      console.error('Login failed:', err);
      toast.error('Login failed. Please try again.');
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    if (BYPASS_AUTH) return;
    try {
      await signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    } finally {
      localStorage.removeItem('intendedPath');
      clearStorage();
    }
  };

  const handleRefreshSession = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.accessToken?.toString() as string;

      if (newToken) {
        localStorage.setItem('accessToken', newToken);
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      console.error('Failed to refresh session:', err);
      toast.error('Session expired. Please login again.');
    } finally {
      await logout();
    }
  };

  const refreshIfExpired = async (token: string | null) => {
    if (BYPASS_AUTH) return;
    const accessTokenParsed = jwtDecode(token ?? '') as CognitoAccessToken;
    const now = Math.floor(Date.now() / 1000);

    if (accessTokenParsed.exp < now) {
      await handleRefreshSession();
    } else {
      initializeUser();
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');

    if (accessToken) {
      (async () => {
        await refreshIfExpired(accessToken);
      })();
    }
  }, []);

  useEffect(() => {
    if (!user?.profile) return;

    Sentry.setUser({
      email: user.profile.email,
      username: user.profile.username,
      ip_address: '{{ auto }}',
    });
  }, [user]);

  const userHasAPIKeys = useMemo(() => {
    return Boolean(user?.profile?.aha_api_key && user.profile.testrail_api_key);
  }, [user]);

  const initializeUser = async (force?: boolean) => {
    if (BYPASS_AUTH) return;
    const account = accountsAPI(logout);

    try {
      if (!force) {
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          console.log('Cached user found!');
          const user = JSON.parse(cachedUser) as InitializeAcc;
          if (user.profile) {
            setUser(user);
            return;
          }
        }
      }

      console.log('Fetching user data from API...');
      const data = await account.getAuthorizedUsers();

      if (data) {
        const userData: InitializeAcc = {
          admin: data.admin,
          profile: data.profile !== null ? data.profile : null,
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      toast.error('Failed to fetch user profile');
    }
  };

  const clearStorage = () => {
    localStorage.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        initializeUser,
        userHasAPIKeys,
        loggingIn,
        setLoggingIn,
        refreshIfExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
